import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Slider, { Settings } from 'react-slick';
import { Modal } from '../../../ui/Modal';
import { BackButtonIconEnum } from '../../../ui/PageHeader/types';
import { headerStyles, modalStyles, SliderWrapper } from './styled';
import { ApartmentModalContent } from './ApartmentModalContent';
import { Apartment } from '../../../../graphql/graphql';
import { PhoneButtonClickHandler } from '../../BottomPhoneButton/types';
import useScreenSize from '../../../../hooks/useScreenSize';
import { DirectionEnum } from '../../../NewSection/types';
import 'slick-carousel/slick/slick.scss';
import 'slick-carousel/slick/slick-theme.css';

interface ApartmentsModalProps {
  isOpen: boolean;
  imgDescription: string;
  phones: string[] | null;
  isVisible: boolean;
  filteredList: Apartment[];
  selectedId: string;
  hide: () => void;
  onClickHandler: PhoneButtonClickHandler;
  setIsVisible: (value: boolean) => void;
  setSelectedId: (id: string) => void;
}

export const ApartmentsModal: React.FC<ApartmentsModalProps> = memo(
  ({
    isOpen,
    hide,
    imgDescription,
    phones,
    isVisible,
    filteredList,
    selectedId,
    setSelectedId,
    onClickHandler,
    setIsVisible
  }) => {
    const { t } = useTranslation();
    const { isSmallOrLargeDesktop } = useScreenSize();
    const sliderRef = useRef<Slider | null>(null);

    const displayableApartments = useMemo(
      () => filteredList.filter(item => item.photos?.length),
      [filteredList]
    );

    const currentIndex = useMemo(
      () => displayableApartments.findIndex(i => i.id === selectedId),
      [displayableApartments, selectedId]
    );

    const settings: Settings = useMemo(
      () => ({
        infinite: true,
        speed: 300,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
        swipe: false,
        draggable: true,
        initialSlide: currentIndex >= 0 ? currentIndex : 0,
        beforeChange: (_, next) => {
          const nextItem = displayableApartments[next];
          if (nextItem) {
            setSelectedId(nextItem.id);
          }
        }
      }),
      [displayableApartments, setSelectedId, currentIndex]
    );

    const handleNavigate = useCallback((direction: DirectionEnum) => {
      if (direction === DirectionEnum.right) {
        sliderRef.current?.slickNext();
      } else {
        sliderRef.current?.slickPrev();
      }
    }, []);

    useEffect(() => {
      if (!isSmallOrLargeDesktop) return;

      const handleKey = (e: KeyboardEvent) => {
        if (e.key === 'ArrowRight') {
          sliderRef.current?.slickNext();
        }
        if (e.key === 'ArrowLeft') {
          sliderRef.current?.slickPrev();
        }
      };

      window.addEventListener('keydown', handleKey);
      return () => window.removeEventListener('keydown', handleKey);
    }, [isSmallOrLargeDesktop]);

    const currentApartment = useMemo(
      () => displayableApartments.find(i => i.id === selectedId),
      [displayableApartments, selectedId]
    );

    const showArrows =
      isSmallOrLargeDesktop && displayableApartments.length > 1;

    return (
      <Modal
        isOpen={isOpen}
        onClose={hide}
        modalId="apartments"
        cypress="apartments"
        title={t('residences.apartments_modal_title')}
        modalStyles={modalStyles}
        headerIcon={BackButtonIconEnum.Close}
        closeButtonAlign="right"
        headerStyles={headerStyles}
        showArrows={showArrows}
        scrollHandler={handleNavigate}
        withAnimation={false}
      >
        {isSmallOrLargeDesktop ? (
          <SliderWrapper>
            <Slider ref={sliderRef} {...settings}>
              {displayableApartments.map(apartment => (
                <ApartmentModalContent
                  key={apartment.id}
                  apartment={apartment}
                  phones={phones}
                  onClickHandler={onClickHandler}
                  setIsVisible={setIsVisible}
                  isVisible={isVisible}
                  imgDescription={imgDescription}
                />
              ))}
            </Slider>
          </SliderWrapper>
        ) : (
          currentApartment && (
            <ApartmentModalContent
              apartment={currentApartment}
              phones={phones}
              onClickHandler={onClickHandler}
              setIsVisible={setIsVisible}
              isVisible={isVisible}
              imgDescription={imgDescription}
            />
          )
        )}
      </Modal>
    );
  }
);

ApartmentsModal.displayName = 'ApartmentsModal';
